import { mdiInformationSlabCircleOutline, mdiPen, mdiPencil } from '@mdi/js';
import HBox from '../layout/HBox';
import { Tooltip } from '../layout/Tooltip';
import styles from './CampoTexto.module.css';
import Icon from '../Icon';
import React from 'react';
import Botao from '../Botao';
import Modal from '../layout/Modal';

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  tooltipText?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  noBorder?: boolean;
  rows?: number;
  instanceId?: string;
  min?: number;
  max?: number;
  canOpenMemoModal?: boolean;
  style?: React.CSSProperties;
  ref?: React.Ref<HTMLTextAreaElement>;
  [key: string]: any;
};

export default function CampoMemo({
  value,
  onChange,
  label,
  placeholder,
  required,
  error,
  tooltipText,
  tooltipPosition,
  noBorder,
  rows,
  instanceId,
  min,
  max,
  canOpenMemoModal,
  style,
  ref,
  ...props
}: Readonly<Props>) {
  rows = rows ?? (value?.split('\n')?.length || 1) + 1;

  const [isMemoModalOpen, setIsMemoModalOpen] = React.useState(false);
  const [memoModalRows, setMemoModalRows] = React.useState(15);
  const closeMemoModal = () => setIsMemoModalOpen(false);
  const openMemoModal = () => {
    // Calcula rows com base na altura disponível no momento da abertura.
    // 75vh = max-height do .content; subtrai padding do content (32px) + label vazia + margem (68px).
    // Divide por 24px que é o line-height real do textarea (font 16px * 1.5).
    const availableHeight = window.innerHeight * 0.75 - 100;
    setMemoModalRows(Math.max(8, Math.floor(availableHeight / 24)));
    setIsMemoModalOpen(true);
  };

  return (
    <div style={{ width: '100%' }}>
      <Modal
        show={isMemoModalOpen}
        onClose={closeMemoModal}
        title={label}
        size='lg'
        hideControls
      >
        <CampoMemo
          value={value}
          onChange={v => onChange?.(v)}
          placeholder={placeholder}
          required={required}
          error={error}
          tooltipText={tooltipText}
          tooltipPosition={tooltipPosition}
          noBorder
          rows={memoModalRows}
        />
      </Modal>

      <label>
        <HBox stretch>
          {canOpenMemoModal ? (<>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {label}
            </span>
            <Botao variant='none-primary' icon={mdiPen} onClick={openMemoModal} title='Expandir' />
          </>
          ) : (
            <span>{label}</span>
          )}
          {tooltipText && (
            <Tooltip text={tooltipText} position={tooltipPosition}>
              <Icon
                path={mdiInformationSlabCircleOutline}
                size={0.8}
                color="var(--tc-color-info)"
              />
            </Tooltip>
          )}
        </HBox>
      </label>
      <textarea
        {...props}
        id={instanceId}
        ref={ref}
        className={[styles.container, error ? styles.error : null].join(' ')}
        style={{
          padding: '4px',
          width: '100%',
          minHeight: '80px',
          border: noBorder ? 'none' : undefined,
          backgroundColor: 'white',
          resize: 'none',
          ...style,
        }}
        rows={rows}
        value={value}
        onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder}
        required={required}
      />

      {(max || error) && (
        <HBox stretch>
          <div className={styles.error}>{error}</div>

          <div className={[
            styles.counter,
            max && (value?.length ?? 0) > max ? styles.error : null,
            min && (value?.length ?? 0) < min ? styles.error : null,
          ].join(' ')}>
            {(value?.length ?? '0')}/{max}
          </div>

        </HBox>
      )}
    </div>
  );
}
